import React, {
    Fragment, useCallback, useState, useRef, useEffect
} from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslator } from '@jutro/locale';
import { MetadataContent } from '@jutro/uiconfig';
import { useAuthentication, RedirectAfterLogin, AUTH_ERRORS } from 'gw-digital-auth-react';
import { Loader, Link } from '@jutro/components';
import { isEmpty } from '../LoginUtil';

import messages from './LoginComponent.messages';
import metadata from './LoginComponent.metadata.json5';
import styles from '../CommonLogin.module.scss';

function LoginComponent(props) {
    const {
        onForgotPassword, onForgotUsername, successPath, loginPath, pathAfterSignIn
    } = props;
    const translator = useTranslator();
    const [formData, updateFormData] = useState({});
    const [loginError, updateLoginError] = useState('');
    const [isSignInButtonClicked, setSignInButtonClicked] = useState(false);
    const [countdown, setCountdown] = useState(30);
    const auth = useAuthentication();
    const { login: authLogin, isAuthInProgress } = useAuthentication();
    const signInRef = useRef({});

    const login = useCallback(() => {
        const username = _.get(formData, 'username');
        const password = _.get(formData, 'password');

        if (isEmpty(username) || isEmpty(password)) {
            updateLoginError('fieldEmpty');
            return Promise.resolve();
        }
        const concatUsername = 'policyholder_portal_'.concat(username);
        updateLoginError(''); // reset errors
        setSignInButtonClicked(true);
        return authLogin({
            username: concatUsername,
            password: password
        })
            .then(() => {
                // reset errors
                updateLoginError('');
            })
            .catch((err) => {
                switch (_.get(err, 'error')) {
                    case AUTH_ERRORS.login_failure: // credentials are wrong
                    case AUTH_ERRORS.notLoggedIn: // tokens check problem
                        updateLoginError('fieldsIncorrect');
                        break;
                    case AUTH_ERRORS.account_locked: {
                        // account is locked
                        updateLoginError('accountLocked');
                        break;
                    }
                    default:
                        updateLoginError('invalidAttempt');
                        break;
                }
            });
    }, [authLogin, formData]);

    const readValue = useCallback(
        (id, path) => {
            return _.get(formData, path);
        },
        [formData]
    );

    const writeValue = useCallback(
        (value, path) => {
            const nextFormData = _.cloneDeep(formData);
            _.set(nextFormData, path, value);
            updateFormData(nextFormData);
        },
        [formData]
    );

    const handleEnterKey = () => {
        signInRef.current.click();
    };
    
    const isOKTAEnabled = true; // Move this before useEffect
    
    useEffect(() => {
        // Only run timer if OKTA is enabled
        if (!isOKTAEnabled) {
            return;
        }
        
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(timer);
                    auth.logout();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [isOKTAEnabled]);
    
    if (isAuthInProgress) {
        return <Loader loaded={!isAuthInProgress} />;
    }

    const overrideProps = {
        '@field': {
            onValueChange: writeValue,
            onEnter: handleEnterKey
        },
        signIn: {
            onTrigger: login,
            innerRef: signInRef
        },
        linkToForgotPassword: {
            visible: !_.isUndefined(onForgotPassword)
        },
        linkToForgotUsername: {
            visible: !_.isUndefined(onForgotPassword)
        },        
        actionsWrapperSingleButton: {
            className: classNames(styles.actionsWrapperSingleButton, {
                [styles.coupleButtonStyle]: !_.isUndefined(onForgotPassword),
                [styles.singleButtonStyle]: _.isUndefined(onForgotPassword)
            })
        },
        errorMsg: {
            content: translator(messages[loginError]),
            visible: !!loginError
        }
    };

    const resolvers = {
        resolveValue: readValue,
        resolveCallbackMap: {
            login: login,
            forgotPassword: onForgotPassword || _.noop,
            forgotUsername: onForgotUsername || _.noop
        },
        resolveClassNameMap: styles
    };

    const successRedirectPath = isSignInButtonClicked ? pathAfterSignIn : successPath;

    return (
        <Fragment>
            <RedirectAfterLogin successPath={successRedirectPath} loginPath={loginPath} />
            {isOKTAEnabled ? (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    padding: '20px'
                }}>
                    <Loader loaded={false} />
                    <p style={{ 
                        marginTop: '20px', 
                        fontSize: '16px',
                        color: '#666'
                    }}>
                        Redirecting to login page in {countdown} seconds...<br/>
                        <center>
                            <Link onClick={auth.logout}>
                                    Back to login
                            </Link>
                        </center>
                    </p>
                </div>
            ) : (
            <MetadataContent
                uiProps={metadata.componentContent}
                overrideProps={overrideProps}
                {...resolvers}
            />
            )}
        </Fragment>
    );
}

LoginComponent.propTypes = {
    onForgotPassword: PropTypes.func,
    onForgotUsername: PropTypes.func,
    successPath: PropTypes.string,
    loginPath: PropTypes.string,
    pathAfterSignIn: PropTypes.string
};

LoginComponent.defaultProps = {
    onForgotPassword: undefined,
    onForgotUsername: undefined,
    successPath: undefined,
    loginPath: undefined,
    pathAfterSignIn: undefined
};

export default LoginComponent;
